import type { CabinetImpersonationBanner } from "@planerius/contracts/cabinet-auth"

import { formatTimeOfDay } from "../lib/format"
import type { ServiceStripe } from "./service-stripes"

/**
 * Из состояния кабинета — в три полосы.
 *
 * Функция чистая намеренно: правила «когда какая полоса» проверяются тестом, а не глазами.
 * Порядок здесь не задаётся — его держит сам компонент полос, и это единственное место,
 * где он описан.
 */

export interface StripeInputs {
  /** Связь есть по мнению браузера. */
  readonly online: boolean
  /** Момент последних полученных данных. Полоса «Нет связи» всегда называет его. */
  readonly dataAt: string | null
  readonly timeZone: string
  readonly impersonation: CabinetImpersonationBanner | null
  readonly tenantName: string
  readonly tenantStatus: "trial" | "active" | "grace" | "suspended" | "archived"
  /** Дата, после которой доступ ограничится. */
  readonly billingDeadline: string | null
}

export interface StripeActions {
  readonly onRetry: () => void
  readonly onExitImpersonation: () => void
  readonly onPay: () => void
}

export function buildStripes(
  inputs: StripeInputs,
  actions: StripeActions,
): readonly ServiceStripe[] {
  const stripes: ServiceStripe[] = []

  if (!inputs.online) {
    // Полоса «Нет связи» ВСЕГДА называет время данных: без него человек не понимает,
    // насколько устарело то, что он видит, и это не всплывающее сообщение,
    // а состояние экрана — оно висит, пока связь не вернётся.
    const shown =
      inputs.dataAt === null
        ? "Данные не загружены"
        : `Показан день на ${formatTimeOfDay(inputs.dataAt, inputs.timeZone)}`
    stripes.push({
      kind: "offline",
      title: "Нет связи",
      text: `${shown}, изменения сейчас не сохраняются`,
      action: { label: "Повторить", onClick: actions.onRetry },
    })
  }

  if (inputs.impersonation !== null) {
    // Скрывать нельзя: режим пишется в журнал действий и предъявляется салону.
    stripes.push({
      kind: "impersonate",
      title: "Режим поддержки",
      text: `Вы работаете в кабинете «${inputs.tenantName}» от имени ${inputs.impersonation.staffDisplayName}`,
      action: { label: "Выйти из режима", onClick: actions.onExitImpersonation },
    })
  }

  /*
   * Заблокированный салон видит отдельный экран, а не полосу: полоса сообщает о том,
   * что ещё можно работать, — а там уже нельзя.
   *
   * ЭКРАН СУЩЕСТВУЕТ И НАЗЫВАЕТСЯ: screens/tenant/suspended-screen.tsx, переключает
   * на него app-shell по статусу салона. Раньше эта фраза была обещанием без экрана,
   * то есть ровно тем, что читается как «уже сделано» и не делается никогда.
   *
   * `grace` — другое состояние: «оплата просрочена, доступ ещё есть», и о нём говорит
   * как раз полоса ниже.
   */
  if (inputs.tenantStatus === "grace") {
    const deadline =
      inputs.billingDeadline === null
        ? "Доступ скоро ограничится"
        : `Доступ ограничится ${inputs.billingDeadline}`
    stripes.push({
      kind: "billing",
      title: "Оплата просрочена",
      text: `${deadline}. Напоминания клиентам продолжат уходить`,
      action: { label: "Оплатить", onClick: actions.onPay },
    })
  }

  // Пробный период тоже показывается полосой (ТЗ §4.6) — но ТОЛЬКО когда известна дата.
  // Полоса без даты занимает 30 пикселей рабочей области каждого салона на триале и не
  // сообщает ровно того, ради чего заводилась: сколько осталось. Сегодня даты в API нет
  // (в CabinetContext у салона есть status, но нет ни конца триала, ни срока отсрочки),
  // поэтому полоса не появляется. Как только поле придёт — она включится сама, без правок.
  if (inputs.tenantStatus === "trial" && inputs.billingDeadline !== null) {
    stripes.push({
      kind: "billing",
      title: "Пробный период",
      text: `Заканчивается ${inputs.billingDeadline}. Записи и напоминания продолжат работать после оплаты`,
      action: { label: "Выбрать тариф", onClick: actions.onPay },
    })
  }

  return stripes
}
